
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useMsal } from "@azure/msal-react";
import {
  AccountInfo,
  InteractionStatus,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { useDispatch } from "react-redux";
import { loginRedirectRequest, tokenRequest } from "../config/authConfig";
import { setAuth, clearAuth } from "../store/auth/reducer";
import { registerUserSession } from "../api/userProfile";

interface AuthContextType {
  user: AccountInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (redirectPath?: string) => Promise<void>;
  logout: (redirectPath?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

let profileApiCalled = false;

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch();

  const account =
    instance.getActiveAccount() || accounts[0] || null;

  const isAuthenticated = !!account;
  const isLoading = inProgress !== InteractionStatus.None;

  // ✅ Acquire token (SSO‑safe)
  const acquireToken = useCallback(async () => {
    if (!account) return;

    try {
      const result = await instance.acquireTokenSilent({
        ...tokenRequest,
        account,
      });

      instance.setActiveAccount(result.account);

      const claims = result.account?.idTokenClaims as any;

      dispatch(
        setAuth({
          token: result.accessToken || result.idToken,
          user: {
            id: result.account?.localAccountId || result.account?.homeAccountId || '',
            email:
              claims?.emailAddress
              || claims?.email
              || account.name
              || result.account?.username
              || '',
            firstName: claims?.given_name || claims?.firstName || '',
            lastName: claims?.family_name || claims?.lastName || '',
            companyName: claims?.extension_CompanyName || claims?.companyName || claims?.company || '',
          },
        })
      );

      // Call POST /appmgr/users/self right after login
      if (!profileApiCalled) {
        profileApiCalled = true;
        registerUserSession().catch(() => {});
      }
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({
          ...tokenRequest,
          account,
        });
      }
    }
  }, [account, instance, dispatch]);

  // ✅ Run after redirect completes
  useEffect(() => {
    if (account && inProgress === InteractionStatus.None) {
      acquireToken();
    }

    if (!account && inProgress === InteractionStatus.None) {
      dispatch(clearAuth());
    }
  }, [account, inProgress, acquireToken, dispatch]);

  // ✅ Redirect-based login (SSO works)
  const login = async (redirectPath?: string) => {
    const requestedPath = redirectPath || window.location.pathname;
    const pathSegments = requestedPath.split('/').filter(Boolean);
    const possibleLang = pathSegments[pathSegments.length - 1];

    if (possibleLang === 'en' || possibleLang === 'fr') {
      sessionStorage.setItem('b2cLoginLang', possibleLang);
    } else {
      sessionStorage.removeItem('b2cLoginLang');
    }

    await instance.loginRedirect(loginRedirectRequest);
  };

  // ✅ Redirect-based logout
  const logout = async (redirectPath?: string) => {
    const requestedPath = redirectPath || window.location.pathname;
    const pathSegments = requestedPath.split('/').filter(Boolean);
    const possibleLang = pathSegments[pathSegments.length - 1];

    const configuredPostLogoutUri =
      process.env.REACT_APP_LOGOUT_REDIRECT_URI
      || process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI
      || window.location.origin;

    const postLogoutUrl = new URL(configuredPostLogoutUri, window.location.origin);
    if (possibleLang === 'en' || possibleLang === 'fr') {
      postLogoutUrl.pathname = `/${possibleLang}`;
    }

    dispatch(clearAuth());

    try {
      sessionStorage.clear();
    } catch (err) {
      console.warn('Failed to clear sessionStorage:', err);
    }

    await instance.logoutRedirect({
      account,
      postLogoutRedirectUri: postLogoutUrl.toString(),
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user: account,
        isAuthenticated,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
